import { useRef, useState } from "react";
import { Divider, Menu, MenuItem } from "@mui/material";
import { Link } from "react-router-dom";
import { RiWhatsappFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import { authSelector, logoutUser } from "../redux/slices/authSlice";
import { walletSelector } from "../redux/slices/walletSlice";
import NotificationModal from "../components/NotificationModal";
import NewStoreConnectWithStoreListModal from "../portals/customer/pages/dashboard/components/NewStoreConnectWithStoreListModal";
import {
  CardIcon,
  HELP_URL,
  HelpIcon,
  LogoutIcon,
  Svg,
  useScrolled,
} from "./AdminTopbar";
import styles from "./AdminTopbar.module.css";
import own from "./CustomerTopbar.module.css";

const WHATSAPP_NUMBER = "31642793793";
const whatsappLink = (store) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi U-Send, I have a question${store ? ` about ${store}` : ""}.`)}`;

const StoreIcon = () => (
  <Svg>
    <path d="M3 9l1.5-5h15L21 9" />
    <path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0" />
    <path d="M5 11v9h14v-9" />
    <path d="M10 20v-5h4v5" />
  </Svg>
);

const BellIcon = () => (
  <Svg>
    <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
  </Svg>
);

const euro = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SettingsIcon = () => (
  <Svg>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
  </Svg>
);

// Customer top bar in the admin look; account menu keeps the existing actions.
export default function CustomerTopbar({ onMenu }) {
  const dispatch = useDispatch();
  const { loggedUser } = useSelector(authSelector);
  const { wallet } = useSelector(walletSelector);
  const [anchorEl, setAnchorEl] = useState(null);
  const barRef = useRef(null);
  const scrolled = useScrolled(barRef);

  const user = loggedUser?.user || {};
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ") || "Your account";
  const initials = name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const balance = Number(wallet?.available_balance) || 0;
  const close = () => setAnchorEl(null);
  const store = loggedUser?.stores?.find((s) => String(s?._id) === String(loggedUser?.active_store));
  const whatsapp = whatsappLink(store?.custom_store_name || store?.store_name);
  const isOwner = user.store_user_role === "admin" || !user.store_user_role;

  return (
    <header ref={barRef} className={`${styles.bar} ${scrolled ? styles.scrolled : ""}`}>
      {onMenu && (
        <button type="button" className={`${styles.iconButton} ${styles.menuButton}`} onClick={onMenu} aria-label="Open menu">
          <Svg>
            <path d="M4 7h16" />
            <path d="M4 12h16" />
            <path d="M4 17h16" />
          </Svg>
        </button>
      )}
      <a className={own.whatsapp} href={whatsapp} target="_blank" rel="noreferrer" title="Chat with U-Send on WhatsApp">
        <RiWhatsappFill aria-hidden="true" />
        <span>WhatsApp us</span>
      </a>
      <a className={styles.iconButton} href={HELP_URL} target="_blank" rel="noreferrer" title="Help centre" aria-label="Help centre">
        <HelpIcon />
      </a>
      <span className={own.notifications}>
        <NotificationModal loggedUser={loggedUser} icon={<BellIcon />} />
      </span>
      <span className={styles.divider} />
      <button
        type="button"
        className={styles.account}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchorEl)}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {user.img ? (
          <img className={styles.avatar} src={user.img} alt="" />
        ) : (
          <span className={`${styles.avatar} ${own.initials}`}>{initials}</span>
        )}
        <span className={styles.text}>
          <span className={styles.name}>{name}</span>
          {user.email && <span className={styles.email}>{user.email}</span>}
        </span>
        <Svg className={styles.chevron}>
          <path d="m6 9 6 6 6-6" />
        </Svg>
      </button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: own.menu, elevation: 0 }}
        MenuListProps={{ className: own.menuList }}
      >
        <div className={own.profile}>
          {user.img ? (
            <img className={own.profileAvatar} src={user.img} alt="" />
          ) : (
            <span className={`${own.profileAvatar} ${own.initials}`}>{initials}</span>
          )}
          <span className={own.profileText}>
            <b>{name}</b>
            <span>{user.email || "No email"}</span>
          </span>
        </div>

        <div className={own.wallet}>
          <span className={own.walletText}>
            <span>Wallet balance</span>
            <b>€ {euro.format(balance)}</b>
          </span>
          <Link to="/customer/payments" className={own.topUp} onClick={close}>
            Top up
          </Link>
        </div>

        <NewStoreConnectWithStoreListModal
          renderTrigger={(openStores) => (
            <MenuItem
              className={own.item}
              // The stores modal lives inside the menu, so the menu stays open behind it.
              onClick={openStores}
            >
              <StoreIcon />
              Stores
              <span className={own.hint}>Switch or add</span>
            </MenuItem>
          )}
        />
        <MenuItem component={Link} to="/customer/payments" onClick={close} className={own.item}>
          <CardIcon />
          Payments
        </MenuItem>
        {isOwner && (
          <MenuItem component={Link} to="/customer/setting" onClick={close} className={own.item}>
            <SettingsIcon />
            Settings
          </MenuItem>
        )}
        <MenuItem component="a" href={whatsapp} target="_blank" rel="noreferrer" onClick={close} className={own.item}>
          <RiWhatsappFill className={own.wa} aria-hidden="true" />
          Chat with us on WhatsApp
        </MenuItem>
        <MenuItem component="a" href={HELP_URL} target="_blank" rel="noreferrer" onClick={close} className={own.item}>
          <HelpIcon />
          Help centre
        </MenuItem>
        <Divider className={own.sep} />
        <MenuItem onClick={() => dispatch(logoutUser())} className={`${own.item} ${own.logout}`}>
          <LogoutIcon />
          Log out
        </MenuItem>
      </Menu>
    </header>
  );
}
